import React, { useState } from 'react';
import { Turnstile } from '@marsidev/react-turnstile';
import { useDarkMode } from '../../context/DarkModeContext';
import { EyeIcon, EyeOffIcon, KeyRound, LoaderCircle, MessageSquare } from 'lucide-react';
import { useTurnstile } from '../hooks';
import { Button, InputField } from '@shared/components';
import { useSelector } from 'react-redux';
import { getCourierSetting, getIsWhatsappConnected } from '@state/courierSlice';

interface LoginFormProps {
  onForgotPassword: () => void;
  onWhatsAppSignIn: () => void;
  onSmsSignIn: () => void;
  onToggleMode: () => void;
  onLogin: (email: string, password: string, turnstileToken?: string, turnstileSiteKey?: string) => Promise<void>;
  primaryColor: string;
  /**
   * Both come from the parent's useAuth instance — the same one whose
   * handleLogin runs the request. Calling useAuth() here would give this form a
   * second, independent copy of the state that nothing ever updates.
   */
  isLoading?: boolean;
  error?: string | null;
  onClearError?: () => void;
  /**
   * Passkey sign-in, owned by the parent for the same reason as above. The
   * email is passed along when the field is filled so the server can narrow
   * the ceremony to that account; left empty, the browser shows its own
   * account picker instead.
   */
  onPasskeySignIn?: (email?: string) => void;
  isPasskeySupported?: boolean;
  isPasskeyLoading?: boolean;
}

const LoginForm = ({
  onForgotPassword,
  onWhatsAppSignIn,
  onSmsSignIn,
  onToggleMode,
  onLogin,
  primaryColor,
  isLoading = false,
  error = null,
  onClearError,
  onPasskeySignIn,
  isPasskeySupported = false,
  isPasskeyLoading = false
}: LoginFormProps) => {
  const { isDarkMode } = useDarkMode();
  const [showPassword, setShowPassword] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });
  const courierSetting = useSelector(getCourierSetting);
  const turnstileSiteKey = courierSetting?.turnstileSiteKey;
  const { siteKey, token: turnstileToken, isReady: turnstileReady, hasError: turnstileError, onSuccess, onError, onExpire, reset: resetTurnstile, widgetRef } = useTurnstile(turnstileSiteKey);
  const isWhatsappConnected = useSelector(getIsWhatsappConnected);
  // Only offer WhatsApp sign-in when the courier's session is actually usable.
  // A logged-in-with-WhatsApp user is otherwise hard-blocked when the gateway
  // is down (no code arrives); hide the button and let them use email/password.
  const isWhatsAppEnabled = courierSetting?.useWaApi && isWhatsappConnected;
  const isSmsApiEnabled = courierSetting?.isSmsApiEnabled;
  // Both halves have to hold: the courier has to have passkeys switched on,
  // and the browser has to be able to run the ceremony at all.
  const isPasskeyAvailable = !!courierSetting?.isPasskeyEnabled && isPasskeySupported;

  // Deliberately not a submit button: the passkey path has no password, so the
  // email/password validation below must not gate it.
  const handlePasskeySignIn = () => {
    onClearError?.();
    onPasskeySignIn?.(formData.email.trim() || undefined);
  };

  // Validation function
  const validateForm = () => {
    const newErrors = {
      email: '',
      password: ''
    };

    // Email validation
    if (!formData.email) {
      newErrors.email = 'Email is required';
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address';
    }

    // Password validation
    if (!formData.password) {
      newErrors.password = 'Password is required';
    } else if (formData.password.length < 6) {
      newErrors.password = 'Password must be at least 6 characters';
    }

    setErrors(newErrors);
    return !newErrors.email && !newErrors.password;
  };

  // Check if form is valid for button state
  const isFormValid = formData.email && formData.password && !errors.email && !errors.password && (turnstileReady || !siteKey) && !turnstileError;

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    // Clear error when user starts typing
    if (errors[name as keyof typeof errors]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }

    // Drop the stale "Invalid email or password" banner as soon as they start
    // correcting the credentials it was complaining about.
    onClearError?.();
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    await onLogin(formData.email, formData.password, turnstileToken || undefined, turnstileSiteKey);
    resetTurnstile();
  };

  return (
    <div className="w-full max-w-md">
      <h2 className="text-2xl font-bold mb-6 text-center text-gray-900 dark:text-gray-100">
        Sign in to your account
      </h2>
      <form className="space-y-4" onSubmit={handleSubmit}>
        {error && (
          <div
            role="alert"
            className="rounded-md border border-red-300 bg-red-50 p-3 text-sm text-red-700 dark:border-red-800 dark:bg-red-900/20 dark:text-red-300"
          >
            {error}
          </div>
        )}
        <div className="space-y-2">
          <InputField
            label="Email"
            name="email"
            type="email"
            value={formData.email}
            onChange={handleInputChange}
            placeholder="Enter your email"
            required
            fullWidth
            darkMode={isDarkMode}
            className={errors.email ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}
          />
          {errors.email && (
            <p className="text-sm text-red-600 mt-1">{errors.email}</p>
          )}
        </div>
        <div className="space-y-2">
          <div className="relative">
            <InputField
              label="Password"
              name="password"
              type={showPassword ? 'text' : 'password'}
              value={formData.password}
              onChange={handleInputChange}
              placeholder="Enter your password"
              required
              fullWidth
              darkMode={isDarkMode}
              className={`pr-10 ${errors.password ? 'border-red-300 focus:border-red-500 focus:ring-red-500' : ''}`}
            />
            <button
              type="button"
              className="absolute inset-y-0 right-0 pr-3 flex items-center top-6"
              onClick={() => setShowPassword(!showPassword)}
            >
              {showPassword ? <EyeOffIcon className="h-5 w-5 text-gray-400" /> : <EyeIcon className="h-5 w-5 text-gray-400" />}
            </button>
          </div>
          {errors.password && (
            <p className="text-sm text-red-600 mt-1">{errors.password}</p>
          )}
        </div>
        <div className="flex items-center justify-end">
          <button
            type="button"
            className="text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            onClick={onForgotPassword}
          >
            Forgot your password?
          </button>
        </div>

        {siteKey && (
          <div className="flex justify-center">
            <Turnstile
              ref={widgetRef}
              siteKey={siteKey}
              onSuccess={onSuccess}
              onError={onError}
              onExpire={onExpire}
              options={{ theme: isDarkMode ? 'dark' : 'light', size: 'flexible' }}
            />
          </div>
        )}
        {turnstileError && (
          <div className="text-center">
            <p className="text-sm text-red-600">CAPTCHA failed to load.</p>
            <button
              type="button"
              onClick={resetTurnstile}
              className="mt-1 text-sm font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
            >
              Try again
            </button>
          </div>
        )}

        {isLoading && (
          <div className="flex justify-center items-center">
            <LoaderCircle className="animate-spin h-5 w-5" />
          </div>
        )}

                        <Button
          type="submit"
          variant="primary"
          disabled={!isFormValid}
          isLoading={isLoading}
          fullWidth
          color={primaryColor}
          textColor="white"
          hoverColor={primaryColor}
          className={`${isFormValid && !isLoading ? '' : 'opacity-50'}`}
        >
          {isLoading ? 'Signing in...' : 'Sign in'}
        </Button>
      </form>
      <div className="mt-6 space-y-4">
        <div className="relative">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-300"></div>
          </div>
          <div className="relative flex justify-center text-sm">
            <span className="px-2 bg-gray-50 text-gray-500 dark:bg-gray-900 dark:text-gray-400">
              Or 
            </span>
          </div>
        </div>
        {isWhatsAppEnabled && (
        <Button
          type="button"
          variant="primary"
          onClick={onWhatsAppSignIn}
          fullWidth
          className="flex items-center justify-center"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24" fill="#25D366" xmlns="http://www.w3.org/2000/svg">
            <path fillRule="evenodd" clipRule="evenodd" d="M20.5027 3.4745C18.2637 1.2355 15.2787 0 12.0642 0C5.4502 0 0.0632 5.387 0.0632 12.001C0.0632 14.115 0.6642 16.177 1.8137 17.972L0 24L6.1612 22.218C7.8887 23.2673 9.9527 23.8213 12.0587 23.8213H12.0642C18.6727 23.8213 24.0002 18.4343 24.0002 11.8203C24.0002 8.6058 22.7417 5.7135 20.5027 3.4745ZM12.0642 21.8248C10.2632 21.8248 8.4952 21.2928 6.9597 20.2873L6.5962 20.0663L2.8447 21.1045L3.9002 17.437L3.6572 17.056C2.5427 15.446 1.9607 13.5545 1.9607 11.6063C1.9607 6.4338 6.4857 2.2538 12.0697 2.2538C14.7152 2.2538 17.1997 3.2703 19.0062 5.0768C20.8127 6.8833 21.8292 9.3678 21.8292 12.0133C21.8237 17.3423 17.2437 21.8248 12.0642 21.8248ZM17.5267 14.5208C17.2232 14.3693 15.7482 13.6473 15.4667 13.5508C15.1852 13.4543 14.9797 13.4048 14.7742 13.7138C14.5687 14.0228 14.0037 14.6898 13.8202 14.8953C13.6367 15.1008 13.4532 15.1228 13.1497 14.9713C12.8462 14.8198 11.8737 14.5043 10.7262 13.4818C9.8322 12.6868 9.2392 11.7088 9.0557 11.4053C8.8722 11.1018 9.0337 10.9293 9.1897 10.7678C9.3292 10.6228 9.5017 10.3923 9.6577 10.2088C9.8137 10.0253 9.8632 9.8848 9.9597 9.6793C10.0562 9.4738 10.0067 9.2903 9.9322 9.1388C9.8577 8.9873 9.2702 7.5123 9.0142 6.9053C8.7582 6.3148 8.4967 6.3893 8.3022 6.3783C8.1187 6.3673 7.9132 6.3673 7.7077 6.3673C7.5022 6.3673 7.1712 6.4418 6.8897 6.7453C6.6082 7.0488 5.8367 7.7708 5.8367 9.2458C5.8367 10.7208 6.9072 12.1408 7.0632 12.3463C7.2192 12.5518 9.2282 15.6453 12.3022 16.9358C13.0077 17.2393 13.5567 17.4173 13.9867 17.5523C14.7082 17.7743 15.3672 17.7413 15.8887 17.6668C16.4652 17.5853 17.6782 16.9468 17.9342 16.2318C18.1902 15.5168 18.1902 14.9098 18.1157 14.8018C18.0412 14.6938 17.8302 14.6193 17.5267 14.4678L17.5267 14.5208Z" />
          </svg>
          Sign in with WhatsApp
        </Button>
        )}
        {isSmsApiEnabled && (
        <Button
          type="button"
          variant="primary"
          onClick={onSmsSignIn}
          fullWidth
          className="flex items-center justify-center"
        >
          <MessageSquare className="w-5 h-5 mr-2" />
          Sign in with SMS
        </Button>
        )}
        {isPasskeyAvailable && (
        <Button
          type="button"
          variant="primary"
          onClick={handlePasskeySignIn}
          fullWidth
          isLoading={isPasskeyLoading}
          className="flex items-center justify-center"
        >
          <KeyRound className="w-5 h-5 mr-2" />
          Sign in with a passkey
        </Button>
        )}
        <Button
          type="button"
          variant="secondary"
          textColor="black"
          className="flex items-center justify-center"
          onClick={onToggleMode}
          fullWidth
        >
          Create account
        </Button>
      </div>
    </div>
  );
};

export default LoginForm;
